<template>
  <div class="p-4">
    <h1 class="mt-2 mb-4">ЛабЧекап онлайн</h1>
    <div class="white-box">
      <div>
        <img
          src="/shub_mascot.jpg"
          alt="ЛабЧекап"
          width="200"
          preview
          class="hidden md:block"
        />
      </div>
      <div v-html="info_data"></div>
    </div>

    <div class="wrapper-block p-4 flex flex-column">
      <div class="mb-4">
        <p>Открытый демонстрационный доступ. Выберите модель и заполните показатели.</p>
        <p v-if="catalogMessage" role="status">{{ catalogMessage }}</p>
        <p-button v-if="!modelsLoading && !availableModels.length" label="Повторить загрузку" @click="loadModels" />
      </div>
      <!-- Выбор модели -->
      <div class="mb-4">
        <label for="modelSelect" class="form-label">Выберите модель</label>
        <p-dropdown
          id="modelSelect"
          v-model="selectedModel"
          :options="availableModels"
          optionLabel="name"
          placeholder="Выберите модель для предсказания"
          class="w-full"
          :disabled="modelsLoading || !availableModels.length"
          @change="onModelChange"
        />
      </div>

      <!-- Общие поля для всех моделей -->
      <div class="common-fields mb-4">
        <div class="mb-3">
          <label for="age" class="form-label">Возраст (лет)</label>
          <p-input-number
            id="age"
            v-model="formData.age"
            class="w-full"
            :min="ageBounds.min"
            :max="ageBounds.max"
            :min-fraction-digits="0"
            :max-fraction-digits="4"
            locale="en-US"
          />
        </div>
        <div class="mb-3">
          <label for="sex" class="form-label">Пол</label>
          <p-dropdown
            id="sex"
            v-model="formData.gender"
            :options="genders"
            optionLabel="name"
            placeholder="Выберите пол"
            class="w-full"
          />
        </div>
      </div>

      <!-- Динамические поля для выбранной модели -->
      <div v-if="selectedModel && modelParameters" class="model-fields mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="(param, key) in modelParameters.input_parameters" :key="key">
          <label :for="param.code" class="block mb-1">
            {{ key }} ({{ param.units }})
            <span v-if="param.min !== undefined || param.max !== undefined" class="text-sm text-gray-500">
              [{{ param.min ?? '—' }}…{{ param.max ?? '—' }}]
            </span>
          </label>
          <p-input-number
            :id="param.code"
            v-model="formData[param.code]"
            class="w-full"
            :min="param.min"
            :max="param.max"
            :min-fraction-digits="0"
            :max-fraction-digits="4"
            locale="en-US"
          />
        </div>
      </div>

      <!-- Кнопка предсказания и результат -->
      <div class="mt-4 flex flex-row gap-3 align-items-center w-full justify-content-between">
        <div>
          <p-button
            label="Проверить"
            @click="predict"
            :disabled="!canPredict"
            :loading="loading"
          />
        </div>
        <div class="flex flex-row gap-3 align-items-center" v-if="predictionResult">
          <div>{{ selectedModel?.name }}:</div>
          <div class="flex flex-column justify-content-center">
            <div class="data-unit">{{ predictionResult }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { promoApi as mlApi } from "@/http";
import pInputNumber from 'primevue/inputnumber';
import pDropdown from 'primevue/dropdown';
import pButton from 'primevue/button';

const loading = ref(false);
const selectedModel = ref(null);
const predictionResult = ref(null);
const modelParameters = ref(null);
const availableModels = ref([]);
const modelsLoading = ref(false);
const catalogMessage = ref('Загружаем модели…');
let catalogVersion = 0;

function clearCatalog() {
  catalogVersion++;
  availableModels.value = [];
  selectedModel.value = null;
  modelParameters.value = null;
  predictionResult.value = null;
  loading.value = false;
  modelsLoading.value = false;
  catalogMessage.value = '';
}


const genders = ref([
  { name: 'Мужчина', value: 1 },
  { name: 'Женщина', value: 0 }
]);

const formData = reactive({
  age: null,
  gender: null,
  uid: 'web-client'
});

// Границы для возраста из метаданных модели (если заданы)
const ageBounds = computed(() => {
  const a = modelParameters.value?.base_parameters?.age;
  return (a && typeof a === 'object') ? { min: a.min, max: a.max } : { min: undefined, max: undefined };
});

// Все ли параметры заполнены (не null/пусто) — иначе валидация на бэке вернёт 422
const canPredict = computed(() => {
  if (loading.value || !selectedModel.value || !modelParameters.value || formData.gender == null) return false;
  if (formData.age == null) return false;
  const params = modelParameters.value?.input_parameters;
  if (params) {
    for (const p of Object.values(params)) {
      const v = formData[p.code];
      if (p.required !== false && (v === null || v === undefined || v === '')) return false;
    }
  }
  return true;
});

const info_data = ref(
  'Выберите модель для предсказания и заполните необходимые поля. Для каждой модели будут доступны только те поля, которые необходимы для предсказания.<br><br>Данный калькулятор не является медицинским изделием и не применим для постановки диагноза без консультации врача. Свидетельство о государственной регистрации программы для ЭВМ № 2025661567 «Интегративная система машинного обучения для прогнозирования концентраций лабораторных тестов второй линии и раннего выявления состояний, заболеваний и факторов риска их развития на основе данных профилактического медицинского осмотра и диспансеризации»'
);

const loadModels = async () => {
  clearCatalog();
  const version = catalogVersion;
  modelsLoading.value = true;
  try {
    const response = await mlApi.get('/models');
    if (version !== catalogVersion) return;
    availableModels.value = Object.entries(response.data).map(([id, data]) => ({
      id,
      name: data.description,
      endpoint: `/predict/${id}`
    }));
    catalogMessage.value = availableModels.value.length ? `Доступно моделей: ${availableModels.value.length}` : 'Пока нет доступных моделей.';
  } catch (error) {
    if (version !== catalogVersion) return;
    catalogMessage.value = error.response?.status === 429 ? 'Слишком много обращений. Повторите загрузку через минуту.' : 'Не удалось загрузить модели. Попробуйте позже.';
  } finally {
    if (version === catalogVersion) modelsLoading.value = false;
  }
};

const onModelChange = async () => {
  if (!selectedModel.value) return;
  const version = catalogVersion;
  const modelId = selectedModel.value.id;
  modelParameters.value = null;
  predictionResult.value = null;
  try {
    const response = await mlApi.get(`/model/${modelId}/parameters`);
    if (version !== catalogVersion || selectedModel.value?.id !== modelId) return;
    modelParameters.value = response.data;
    
    // Очищаем старые значения полей
    Object.keys(formData).forEach(key => {
      if (!['age', 'gender', 'uid'].includes(key)) {
        delete formData[key];
      }
    });
    
    // Инициализируем новые поля пустыми (null), чтобы пользователь ввёл значения,
    // а не отправил 0 (для моделей со строгими границами, напр. aptt, это даст 422)
    Object.values(response.data.input_parameters).forEach(param => {
      formData[param.code] = null;
    });
  } catch (error) {
    if (version === catalogVersion && selectedModel.value?.id === modelId) catalogMessage.value = 'Не удалось загрузить параметры модели.';
  }
};

const predict = async () => {
  if (!canPredict.value) return;
  
  loading.value = true;
  predictionResult.value = null;
  const version = catalogVersion;
  const modelId = selectedModel.value.id;

  try {
    const requestData = {
      uid: formData.uid,
      age: formData.age,
      gender: formData.gender.value
    };

    // Добавляем специфичные поля модели
    if (modelParameters.value) {
      Object.values(modelParameters.value.input_parameters).forEach(param => {
        if (formData[param.code] != null || param.required !== false) requestData[param.code] = formData[param.code];
      });
    }

    const response = await mlApi.post(selectedModel.value.endpoint, requestData);
    if (version !== catalogVersion || selectedModel.value?.id !== modelId) return;
    const value = response.data.prediction;
    const names = {color: 'Цвет', density: 'Плотность', ph: 'pH', epithelium: 'Плоский эпителий', wbc: 'Лейкоциты', rbc: 'Эритроциты'};
    predictionResult.value = typeof value === 'number' ? value.toFixed(2)
      : value && typeof value === 'object' ? Object.entries(names).filter(([key]) => value[key]?.value !== undefined).map(([key, label]) => `${label}: ${value[key].value}`).join('\n')
      : 'Значение не получено';
  } catch (error) {
    if (version !== catalogVersion || selectedModel.value?.id !== modelId) return;
    if (error?.response?.status === 429) {
      const seconds = Number(error.response.headers?.['retry-after']);
      predictionResult.value = Number.isFinite(seconds) && seconds > 0
        ? `Лимит демодоступа. Повторите через ${Math.ceil(seconds / 60)} мин.`
        : 'Слишком много запросов. Повторите через минуту.';
      return;
    }
    const detail = error?.response?.data?.detail;
    predictionResult.value = typeof detail === "string"
      ? `Ошибка: ${detail}`
      : "Ошибка при получении предсказания";
  } finally {
    if (version === catalogVersion) loading.value = false;
  }
};
onMounted(loadModels);
</script>

<style>
.wrapper-block {
  width: 100%;
  min-height: 80px;
  border-radius: 5px;
  background: #ffffff;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.1);
}

.data-unit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  border: 1px solid #e5f0ff;
  background: #fafcff;
  min-height: 48px;
  white-space: pre-line;
  padding-top: 10px;
  padding-bottom: 10px;
  flex-shrink: 0;
  padding-left: 12px;
  padding-right: 12px;
  min-width: 120px;
}

.white-box {
  display: flex;
  width: 100%;
  min-height: 100px;
  padding: 20px;
  align-items: center;
  gap: 17px;
  flex-shrink: 0;
  border-radius: 5px;
  background: #ffffff;
  margin-top: 30px;
  margin-bottom: 40px;
  color: #000000;
  font-family: Nunito;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

/* Стили для компонентов PrimeVue */
:deep(.p-inputnumber) {
  width: 100%;
}

:deep(.p-inputnumber-input),
:deep(.p-dropdown),
:deep(.p-dropdown-trigger) {
  border-radius: 5px;
  border: 1px solid #e5f0ff;
  background: #fafcff;
}

:deep(.p-dropdown-trigger) {
  border: none;
  border-left: 1px solid #e5f0ff;
}

:deep(.p-button) {
  background: #177d83;
  border: none;
  &:enabled:hover {
    background: #11646a;
  }
}

.form-label {
  display: block;
  margin-bottom: 8px;
  color: #5b5b5b;
  font-weight: 500;
}

.model-fields {
  background: #fafcff;
  padding: 20px;
  border-radius: 5px;
  border: 1px solid #e5f0ff;
}

/* Адаптивные стили */
@media screen and (max-width: 768px) {
  .white-box {
    flex-direction: column;
    text-align: center;
  }
  
  .data-unit {
    width: 100%;
  }
}
</style> 
